import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { FormTemplate } from 'data/template/template.types';
import { FieldUpdateHandlerParam } from 'shared/form';
import { FormLayoutContent, FormLayout } from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import SipSettingForm from 'routes/SipSetting/components/SipSettingForm';
import { Ref } from 'routes/SipSetting/components/SipSettingForm/SipSettingForm';
import { RootState } from 'data/root.reducers';
import * as sipSettingSelectors from 'data/sipSetting/sipSetting.selectors';
import * as sipSettingActions from 'data/sipSetting/sipSetting.actions';
import * as dfniActions from 'data/deliveryFunctionNetworkInterface/deliveryFunctionNetworkInterface.actions';
import * as dfniSelectors from 'data/deliveryFunctionNetworkInterface/deliveryFunctionNetworkInterface.selectors';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import {
  SipInfo,
  SipSetting,
  SipSettingSection,
  SipTimer
} from 'data/sipSetting/sipSetting.types';
import { useSipSettingFieldCreateNew } from 'routes/SipSetting/hooks/useSipSettingFieldCreateNew';
import { SIP_SETTING, SYSTEM, SYSTEM_CONFIGURATION_ROUTE } from '../../../../constants';
import { sipSettingTemplate } from '../../templates';
import {
  adaptTemplateVisibilityMode,
  adaptTemplateOptions,
  adaptTemplateFieldChanges
} from '../../utils/sipSettingTemplate.adapter';
import {
  getAuditDetailsToSubmit
} from '../../utils/sipSettingDataToSubmit';

type RouterParams = {
  history: string
}

const mapState = (state: RootState) => ({
  deliveryFunctionId: state.global.selectedDF,
  selectedSipSetting: sipSettingSelectors
    .getSelectedSipSetting(state)[0],
  originalSelectedSipSetting: sipSettingSelectors
    .getSelectedSipSetting(state)[0],
  sipSetting: sipSettingSelectors.getSipSetting(state),
  sipSettingAdaptedTemplate: adaptTemplateVisibilityMode(sipSettingTemplate, 'edit'),
  deliveryFunctionNetworkInterfaces: dfniSelectors.getDeliveryFunctionNetworkInterfaceList(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  userId: authenticationSelectors.getAuthenticationUserId(state),
});

const mapDispatch = {
  goToSipSettingView: sipSettingActions.goToSipSettingView,
  getSipSetting: sipSettingActions.getSipSetting,
  getDfnis: dfniActions.getDeliveryFunctionNetworkInterfaceList,
  createSipSetting: sipSettingActions.createSipSetting,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter;

export const SipSettingCreate = ({
  history,
  deliveryFunctionId,
  sipSetting,
  sipSettingAdaptedTemplate,
  deliveryFunctionNetworkInterfaces,
  isAuditEnabled,
  userName,
  userId,
  getSipSetting,
  goToSipSettingView,
  getDfnis,
  createSipSetting,
}: Props): JSX.Element => {

  const formRef = useRef<Ref>(null);
  const [formData, setFormData] = useState<Partial<SipSetting>>({});
  const [template, setTemplate] = useState<FormTemplate>(sipSettingAdaptedTemplate as FormTemplate);
  const { t } = useTranslation();

  const [handleFieldCreateNew] = useSipSettingFieldCreateNew(
    (field, filedSection) => handleFieldUpdate(field, filedSection),
  );

  useEffect(() => {
    getSipSetting(deliveryFunctionId);
    getDfnis(deliveryFunctionId)
  }, [deliveryFunctionId, getSipSetting, getDfnis]);

  useEffect(() => {
    if (sipSettingAdaptedTemplate == null) {
      return;
    }

    // NOTE: Because there is only one Sip Setting, if at least one already exists DX goes to edit page
    if (sipSetting?.id != null || sipSetting?.sipInfo?.id != null) {
      return history.push(`${SYSTEM_CONFIGURATION_ROUTE}${SYSTEM}${SIP_SETTING}/edit`);
    }

    let clonedTemplate = cloneDeep(sipSettingAdaptedTemplate) as FormTemplate;
    clonedTemplate = adaptTemplateOptions(clonedTemplate, deliveryFunctionNetworkInterfaces) as FormTemplate;
    clonedTemplate.formId = Date.now().toString();

    setTemplate(clonedTemplate as FormTemplate);

  }, [sipSetting, sipSettingAdaptedTemplate, deliveryFunctionNetworkInterfaces, history]);

  const handleCancel = () => goToSipSettingView(history);

  const handleClickSave = () => {
    if (formRef.current === undefined || formRef.current === null) return null;

    if (!formRef.current.isValid(true, true)) {
      return null;
    }

    const sipSettingData = formRef.current.getValues(false);

    const payload = {
      data: sipSettingData,
      auditDetails: getAuditDetailsToSubmit(
        sipSettingData,
        sipSetting,
        isAuditEnabled,
        userId,
        userName,
        'CREATE',
      ),
    };

    createSipSetting(deliveryFunctionId, payload, history);
  };

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam, filedSection: SipSettingSection) => {

    setTemplate(adaptTemplateFieldChanges(template, fieldName, value));

    const newFormData = cloneDeep(formRef?.current?.getValues(true));

    if (filedSection === SipSettingSection.sipTimer) {
      if (newFormData) {
        newFormData.sipTimer[fieldName as keyof SipTimer] = value;
        setFormData(newFormData);
      }
    } else if (filedSection === SipSettingSection.sipInfo) {
      if (newFormData) {
        newFormData.sipInfo[fieldName as keyof SipInfo] = value;
        setFormData(newFormData);
      }
    }
  };

  const renderForm = () => {
    if (template) {
      return (
        <SipSettingForm
          ref={formRef}
          data={formData}
          template={template}
          isEditing
          onFieldUpdate={handleFieldUpdate}
          onFieldCreateNew={handleFieldCreateNew}
        />
      );
    }
  };

  const renderFormPanel = () => {
    return (
      <FormPanel title={t('sipSetting.create.title')}>
        <FormButtons
          isEditing
          onClickCancel={handleCancel}
          onClickSave={handleClickSave}
          onClickClose={handleCancel}
        />
      </FormPanel>
    );
  };

  return (
    <div data-testid="SipSettingCreate">
      <FormLayout>
        <FormLayoutContent>
          {renderFormPanel()}
          {renderForm()}
        </FormLayoutContent>
      </FormLayout>
    </div>
  );
};

export default withRouter(connector(SipSettingCreate));
